import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { thinkingModes } from '../data/modes';
import { studyPurposes } from '../data/purposes';
import { toolTypes } from '../data/toolTypes';
import { getDashboardRecommendation } from '../data/dashboardRecommendations';
import ScrollToTop from '../utils/ScrollToTop';

const { FiMail, FiUser, FiShield, FiArrowRight, FiCheckCircle, FiAlertCircle } = FiIcons;

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

const IS_DEV = import.meta.env.DEV;
const API_URL = '/mathe-app/api/contact.php';

export default function RegistrationForm({ modeId, purposeId, toolId, scores, onComplete }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  const mode = thinkingModes[modeId] || thinkingModes.structure;
  const purpose = studyPurposes[purposeId] || studyPurposes.sprint;
  const tool = toolTypes[toolId] || toolTypes.systematic;
  const rec = getDashboardRecommendation(modeId, toolId);

  const validateAll = () => {
    let valid = true;
    if (!name.trim()) { setNameError('Bitte Namen eingeben.'); valid = false; }
    if (!email.trim()) { setEmailError('Bitte E-Mail-Adresse eingeben.'); valid = false; }
    else if (!isValidEmail(email)) { setEmailError('Bitte eine gültige E-Mail-Adresse eingeben.'); valid = false; }
    return valid;
  };

  const submitToServer = async () => {
    const payload = new FormData();
    payload.append('name', name.trim());
    payload.append('email', email.trim());
    payload.append('mode', mode.title);
    payload.append('purpose', purpose.title);
    payload.append('tool', tool.title);
    // Empfehlungen als kommagetrennte Listen
    payload.append('boards', (rec?.boards ?? []).join(', '));
    payload.append('card_types', (rec?.cardTypes ?? []).join(', '));
    payload.append('tools', (rec?.tools ?? []).join(', '));
    // Quiz-Scores
    payload.append('scores_mode', JSON.stringify(scores?.mode ?? {}));
    payload.append('scores_math', JSON.stringify(scores?.math ?? {}));
    payload.append('scores_tool', JSON.stringify(scores?.tool ?? {}));
    payload.append('website', '');

    const response = await fetch(API_URL, { method: 'POST', body: payload });
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Server antwortet nicht mit JSON. HTTP ${response.status}.`);
    }
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.message || 'Versand fehlgeschlagen.');
  };

  const simulateSuccess = () => new Promise((resolve) => setTimeout(resolve, 1200));

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!validateAll()) return;
    setStatus('loading');
    setErrorMessage('');
    try {
      if (IS_DEV) { await simulateSuccess(); } else { await submitToServer(); }
      setStatus('success');
      setTimeout(() => onComplete({ name, email }), 2000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Verbindung zum Server fehlgeschlagen.');
    }
  };

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto pt-12 text-center"
      >
        <ScrollToTop />
        <div className="bg-slate-900 border-4 border-emerald-500/50 rounded-[48px] p-16 shadow-2xl">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheckCircle} className="text-4xl text-emerald-400" />
          </div>
          <h2 className="text-3xl font-black text-white mb-4">Anfrage gesendet!</h2>
          <p className="text-slate-300">
            Deine Daten wurden sicher übertragen. Dein Lernvertrag wurde an{' '}
            <strong className="text-white">{email}</strong> gesendet.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto pt-12"
    >
      <ScrollToTop />
      <div className="bg-slate-900 border-4 border-slate-800 rounded-[48px] p-10 md:p-16 shadow-2xl relative overflow-hidden">
        <div className="relative z-10">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-black text-white mb-4">Mission freischalten</h2>
            <p className="text-slate-300 leading-relaxed">
              Sende uns dein{' '}
              <span className="text-blue-400 font-bold">{mode.title}</span>-Profil mit Tool-Stack{' '}
              <span className="text-emerald-400 font-bold">{tool.title}</span>.
            </p>
            <p className="text-slate-500 text-sm mt-2">
              Du erhältst deinen vollständigen Lernvertrag per E-Mail – druckfertig.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate autoComplete="off" className="space-y-6">
            <input type="text" name="website" tabIndex={-1} aria-hidden="true"
              style={{ display: 'none' }} readOnly value="" />

            <div className="space-y-2">
              <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 ml-4">
                Dein Name
              </label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text" name="fullname" autoComplete="off"
                  placeholder="Max Mustermann"
                  className={`w-full bg-slate-800 border-2 rounded-2xl py-5 pl-14 pr-6 text-white outline-none transition-all ${nameError ? 'border-red-500' : 'border-slate-700 focus:border-blue-500'}`}
                  value={name}
                  onChange={(e) => { setName(e.target.value); if (nameError && e.target.value.trim()) setNameError(''); }}
                  onBlur={() => { if (!name.trim()) setNameError('Bitte Namen eingeben.'); else setNameError(''); }}
                />
              </div>
              {nameError && (
                <p className="text-red-400 text-xs ml-4 flex items-center gap-1">
                  <SafeIcon icon={FiAlertCircle} /> {nameError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 ml-4">
                Deine E-Mail
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text" name="usermail" autoComplete="off"
                  autoCorrect="off" autoCapitalize="off" spellCheck={false}
                  placeholder="name@beispiel.de"
                  className={`w-full bg-slate-800 border-2 rounded-2xl py-5 pl-14 pr-6 text-white outline-none transition-all ${emailError ? 'border-red-500 focus:border-red-400' : 'border-slate-700 focus:border-blue-500'}`}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailError && isValidEmail(e.target.value)) setEmailError(''); }}
                  onBlur={() => {
                    if (!email.trim()) setEmailError('Bitte E-Mail-Adresse eingeben.');
                    else if (!isValidEmail(email)) setEmailError('Bitte eine gültige E-Mail-Adresse eingeben.');
                    else setEmailError('');
                  }}
                />
              </div>
              {emailError && (
                <p className="text-red-400 text-xs ml-4 flex items-center gap-1">
                  <SafeIcon icon={FiAlertCircle} /> {emailError}
                </p>
              )}
            </div>

            {status === 'error' && (
              <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                <SafeIcon icon={FiAlertCircle} /> {errorMessage}
              </div>
            )}

            <div className="flex items-start gap-3 p-5 bg-blue-500/5 rounded-2xl border border-blue-500/20">
              <SafeIcon icon={FiShield} className="text-blue-400 mt-1 shrink-0" />
              <p className="text-xs text-slate-400 leading-relaxed">
                Mit dem Absenden erhältst du deinen persönlichen Lernvertrag per E-Mail –
                inklusive Begleiter-Verpflichtungen, druckfertig zum Unterzeichnen.
                Deine Daten werden vertraulich gemäß DSGVO behandelt.
              </p>
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-6 bg-white text-slate-900 font-black rounded-3xl text-xl flex items-center justify-center gap-4 hover:bg-slate-100 transition-all shadow-xl disabled:opacity-50"
            >
              {status === 'loading' ? 'Wird gesendet...' : 'Jetzt Mission anfragen'}
              {status !== 'loading' && <SafeIcon icon={FiArrowRight} />}
            </button>
          </form>
        </div>
      </div>
    </motion.div>
  );
}